import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileText, CheckCircle2 } from "lucide-react";

/**
 * R153 — Public estoppel request form for title companies.
 *
 * URL: /estoppel-request/:propertyId
 *
 * Title companies + closing agents can submit a request without an account.
 * Admins review the queue at /admin/estoppel and either generate + email or
 * decline. No authentication required — POST goes to a captcha-free public
 * endpoint that rate-limits by IP.
 */
export default function PublicEstoppelRequestPage() {
  const { propertyId } = useParams();
  const [prop, setProp] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    unit: "",
    buyer_name: "",
    seller_name: "",
    title_company: "",
    title_agent_name: "",
    to_email: "",
    phone: "",
    closing_date: "",
    notes: "",
  });

  useEffect(() => {
    fetch(`${API}/public/estoppel/property/${propertyId}`)
      .then((r) => r.json())
      .then((d) => setProp(d))
      .catch(() => setProp({ error: "not_found" }));
  }, [propertyId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.unit || !form.to_email || !form.title_company) {
      toast.error("Unit, title company, and email are required");
      return;
    }
    setBusy(true);
    try {
      const resp = await fetch(`${API}/public/estoppel/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ property_id: propertyId, ...form }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setSubmitted(true);
    } catch {
      toast.error("Request failed — please email the property manager directly.");
    } finally { setBusy(false); }
  };

  if (prop?.error === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0b] p-6">
        <Card className="border-white/10 p-8 max-w-md">
          <FileText className="h-8 w-8 text-rose-400 mb-4" />
          <h2 className="text-lg font-semibold text-white mb-1">Community not found</h2>
          <p className="text-sm text-white/60">The link you followed may be out of date. Please contact the property manager for a fresh estoppel request link.</p>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0b] p-6"
           data-testid="estoppel-request-success">
        <Card className="border-emerald-500/30 bg-emerald-500/5 p-8 max-w-md text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-white mb-2">Request submitted</h2>
          <p className="text-sm text-white/70">
            The property manager has been notified. You'll receive the completed
            estoppel certificate at <strong>{form.to_email}</strong> within
            1–3 business days.
          </p>
          <p className="text-xs text-white/40 mt-4">
            Reference: {prop?.name} · Unit {form.unit}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white p-6">
      <div className="max-w-2xl mx-auto py-8">
        <header className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 text-orange-300 text-xs mb-3">
            <FileText className="h-3 w-3" /> Estoppel / Resale Certificate
          </div>
          <h1 className="text-3xl font-semibold mb-1">Request an Estoppel</h1>
          {prop?.name && (
            <p className="text-white/60">
              {prop.name}{prop.address ? ` · ${prop.address}` : ""}
            </p>
          )}
          <p className="text-sm text-white/50 mt-3 max-w-lg">
            Title companies + closing agents: submit the details below and the
            property manager will email the completed certificate PDF within
            1–3 business days.
          </p>
        </header>

        <form onSubmit={submit} className="space-y-4"
              data-testid="estoppel-request-form">
          <Card className="border-white/10 bg-white/[0.02] p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80">Unit number *</Label>
                <Input value={form.unit} required
                        onChange={(e) => setForm({ ...form, unit: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 text-white placeholder:text-white/30"
                        data-testid="request-unit" />
              </div>
              <div>
                <Label className="text-white/80">Scheduled closing date</Label>
                <Input type="date" value={form.closing_date}
                        onChange={(e) => setForm({ ...form, closing_date: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 text-white [color-scheme:dark]"
                        data-testid="request-closing-date" />
              </div>
              <div>
                <Label className="text-white/80">Buyer (of record)</Label>
                <Input value={form.buyer_name}
                        onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 text-white placeholder:text-white/30"
                        data-testid="request-buyer" />
              </div>
              <div>
                <Label className="text-white/80">Seller (of record)</Label>
                <Input value={form.seller_name}
                        onChange={(e) => setForm({ ...form, seller_name: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 text-white placeholder:text-white/30"
                        data-testid="request-seller" />
              </div>
            </div>
          </Card>

          <Card className="border-white/10 bg-white/[0.02] p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80">Title company *</Label>
                <Input value={form.title_company} required
                        onChange={(e) => setForm({ ...form, title_company: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 text-white placeholder:text-white/30"
                        data-testid="request-title-co" />
              </div>
              <div>
                <Label className="text-white/80">Title agent name</Label>
                <Input value={form.title_agent_name}
                        onChange={(e) => setForm({ ...form, title_agent_name: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 text-white placeholder:text-white/30"
                        data-testid="request-title-agent" />
              </div>
              <div>
                <Label className="text-white/80">Delivery email *</Label>
                <Input type="email" value={form.to_email} required
                        onChange={(e) => setForm({ ...form, to_email: e.target.value })}
                        placeholder="closings@titleco.com"
                        className="bg-white/[0.04] border-white/10 mt-1 text-white placeholder:text-white/30"
                        data-testid="request-email" />
              </div>
              <div>
                <Label className="text-white/80">Phone (optional)</Label>
                <Input value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 text-white placeholder:text-white/30"
                        data-testid="request-phone" />
              </div>
            </div>

            <div>
              <Label className="text-white/80">Notes / special instructions</Label>
              <Textarea value={form.notes} rows={3}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 text-sm text-white placeholder:text-white/30"
                        data-testid="request-notes" />
            </div>
          </Card>

          <Button type="submit" disabled={busy}
                  className="bg-orange-500 hover:bg-orange-600 rounded-full h-11 w-full text-base"
                  data-testid="request-submit-btn">
            {busy ? "Submitting…" : "Submit request"}
          </Button>
          <p className="text-xs text-white/40 text-center">
            The property manager will review your request and email the estoppel PDF within 1–3 business days.
          </p>
        </form>
      </div>
    </div>
  );
}
